import React from "react";
import {
  Plus,
  Globe,
  Building2,
  Bookmark,
  CheckCircle2,
  ArrowRight,
  Sparkles,
} from "lucide-react";

function LeftSidebar({
  activeFilter,
  setActiveFilter,
  selectedCommunity,
  setSelectedCommunity,
  onOpenCreateModal,
}) {
  const myCommunities = [
    {
      id: "bansal",
      name: "Bansal Institute of Engineering & Tech",
      verified: true,
      members: "1.2K",
      iconColor: "bg-blue-600",
    },
    {
      id: "mera",
      name: "Mera College, Pune",
      verified: false,
      members: "985",
      iconColor: "bg-teal-600",
    },
    {
      id: "lnct",
      name: "LNCT University",
      verified: false,
      members: "864",
      iconColor: "bg-orange-500",
    },
  ];

  const popularCommunities = [
    {
      id: "iitb",
      name: "IIT Bombay",
      verified: true,
      members: "8.4K",
      iconColor: "bg-amber-600",
    },
    {
      id: "du",
      name: "Delhi University",
      verified: true,
      members: "7.2K",
      iconColor: "bg-purple-600",
    },
    {
      id: "bits",
      name: "BITS Pilani",
      verified: true,
      members: "6.1K",
      iconColor: "bg-red-500",
    },
  ];

  const filters = [
    { id: "all", label: "All Posts", icon: Sparkles },
    { id: "public", label: "Public Posts", icon: Globe },
    { id: "college", label: "My College Posts", icon: Building2 },
    { id: "saved", label: "Saved Posts", icon: Bookmark },
  ];

  return (
    <aside className="space-y-6">
      {/* My Communities Card */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100/80 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 text-base">My Communities</h3>
          <button
            onClick={onOpenCreateModal}
            title="Create or join community"
            className="w-7 h-7 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 flex items-center justify-center transition-colors"
          >
            <Plus size={18} />
          </button>
        </div>

        <div className="space-y-2">
          {myCommunities.map((comm) => {
            const isSelected = selectedCommunity === comm.id;
            return (
              <button
                key={comm.id}
                onClick={() =>
                  setSelectedCommunity(isSelected ? null : comm.id)
                }
                className={`w-full text-left p-3 rounded-xl flex items-center space-x-3 transition-all ${
                  isSelected
                    ? "bg-purple-50/80 border border-purple-200 shadow-xs"
                    : "hover:bg-gray-50 border border-transparent"
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-xl ${comm.iconColor} text-white flex items-center justify-center font-bold text-sm shrink-0 shadow-xs`}
                >
                  {comm.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-1">
                    <span
                      className={`text-sm font-semibold truncate ${
                        isSelected ? "text-purple-900" : "text-gray-800"
                      }`}
                    >
                      {comm.name}
                    </span>
                    {comm.verified && (
                      <CheckCircle2
                        size={15}
                        className="text-blue-500 fill-blue-500 text-white shrink-0"
                      />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 font-medium">
                    {comm.members} members
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        <button className="w-full mt-4 pt-3 border-t border-gray-100 text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center justify-between transition-colors">
          <span>View All Communities</span>
          <ArrowRight size={14} />
        </button>
      </div>

      {/* Filters Card */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100/80 hover:shadow-md transition-shadow">
        <h3 className="font-bold text-gray-900 text-sm mb-3">Filters</h3>
        <div className="space-y-1">
          {filters.map((filter) => {
            const Icon = filter.icon;
            const isActive = activeFilter === filter.id;
            return (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl font-medium text-sm transition-all ${
                  isActive
                    ? "bg-purple-50 text-purple-700 shadow-xs font-semibold"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <Icon
                  size={18}
                  className={isActive ? "text-purple-600" : "text-gray-400"}
                />
                <span>{filter.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Popular Communities Card */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100/80 hover:shadow-md transition-shadow">
        <h3 className="font-bold text-gray-900 text-base mb-4">
          Popular Communities
        </h3>
        <div className="space-y-3">
          {popularCommunities.map((comm) => (
            <div
              key={comm.id}
              className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer"
              onClick={() => setSelectedCommunity(comm.id)}
            >
              <div
                className={`w-9 h-9 rounded-xl ${comm.iconColor} text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-xs`}
              >
                {comm.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-1">
                  <span className="text-sm font-semibold text-gray-800 truncate">
                    {comm.name}
                  </span>
                  {comm.verified && (
                    <CheckCircle2
                      size={14}
                      className="text-blue-500 fill-blue-500 text-white shrink-0"
                    />
                  )}
                </div>
                <p className="text-xs text-gray-500 font-medium">
                  {comm.members} members
                </p>
              </div>
            </div>
          ))}
        </div>

        <button className="w-full mt-4 pt-3 border-t border-gray-100 text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center justify-between transition-colors">
          <span>Explore More</span>
          <ArrowRight size={14} />
        </button>
      </div>
    </aside>
  );
}

export default LeftSidebar;
